import React from "react";
import { Modal } from "antd";
import {
  ClockCircleOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import {
  formatDateToLocal,
  formatNumber,
  getRecipientLabel,
  getStatusLabel,
  getTransactionTitle,
  isPendingStatus,
  isRejectedStatus,
} from "./helpers";

const renderStatusPill = (status) => {
  const label = getStatusLabel(status);
  if (isPendingStatus(status)) {
    return (
      <span className="transactionStatusPill transactionStatusPillPending">
        <ClockCircleOutlined />
        <span>{label}</span>
      </span>
    );
  }
  if (isRejectedStatus(status)) {
    return (
      <span className="transactionStatusPill transactionStatusPillRejected">
        <CloseCircleOutlined />
        <span>{label}</span>
      </span>
    );
  }
  return (
    <span className="transactionStatusPill transactionStatusPillCompleted">
      <CheckCircleOutlined />
      <span>{label}</span>
    </span>
  );
};

const renderAmount = (record) => (
  <span
    className={`transactionAmountValue ${
      isPendingStatus(record?.status)
        ? "transactionAmountPending"
        : isRejectedStatus(record?.status)
          ? "transactionAmountRejected"
          : ""
    }`}
  >
    {record?.amount != null
      ? `${formatNumber(record.amount, 2)} ${record.currency || ""}`
      : "—"}
  </span>
);

const renderRecipientGetsAmount = (record) => (
  <span
    className={`transactionAmountValue ${
      isPendingStatus(record?.status)
        ? "transactionAmountPending"
        : isRejectedStatus(record?.status)
          ? "transactionAmountRejected"
          : ""
    }`}
  >
    {record?.receivedAmount != null && record?.targetCurrency
      ? `${formatNumber(record.receivedAmount, 2)} ${record.targetCurrency}`
      : "—"}
  </span>
);

const DetailModal = ({ open, onCancel, selectedTransaction, isMobile }) => (
  <Modal
    title={
      selectedTransaction
        ? getTransactionTitle(selectedTransaction)
        : "Transaction Details"
    }
    open={open}
    onCancel={onCancel}
    footer={null}
    className="transaction-details-modal"
    width={isMobile ? "95%" : 520}
  >
    {selectedTransaction && (
      <div className="transaction-modal-content">
        <div className="transaction-modal-summary">
          <div className="transaction-modal-summaryTop">
            {renderStatusPill(selectedTransaction.status)}
          </div>
        </div>
        <div className="transaction-modal-divider" />
        <div className="transaction-modal-row">
          <span className="transaction-modal-label">Reference ID</span>
          <span className="transaction-modal-value">
            {selectedTransaction.referenceID || "N/A"}
          </span>
        </div>
        <div className="transaction-modal-row">
          <span className="transaction-modal-label">Details</span>
          <span className="transaction-modal-value">
            {getRecipientLabel(selectedTransaction)}
          </span>
        </div>
        <div className="transaction-modal-row">
          <span className="transaction-modal-label">Status</span>
          <span className="transaction-modal-value">
            {String(selectedTransaction.status || "N/A")}
          </span>
        </div>
        <div className="transaction-modal-divider" />
        <div className="transaction-modal-row">
          <span className="transaction-modal-label">Date</span>
          <span className="transaction-modal-value">
            {formatDateToLocal(selectedTransaction.date)}
          </span>
        </div>
        <div className="transaction-modal-row">
          <span className="transaction-modal-label">Completion Date</span>
          <span className="transaction-modal-value">
            {selectedTransaction.completionDate
              ? formatDateToLocal(selectedTransaction.completionDate)
              : "Not yet completed"}
          </span>
        </div>
        <div className="transaction-modal-divider" />
        <div className="transaction-modal-row">
          <span className="transaction-modal-label">Amount</span>
          {renderAmount(selectedTransaction)}
        </div>
        {selectedTransaction.receivedAmount != null &&
        selectedTransaction.targetCurrency ? (
          <div className="transaction-modal-row">
            <span className="transaction-modal-label">Recipient Gets</span>
            {renderRecipientGetsAmount(selectedTransaction)}
          </div>
        ) : null}
        {selectedTransaction.conversionRateFinal != null ? (
          <div className="transaction-modal-row">
            <span className="transaction-modal-label">Exchange Rate</span>
            <span className="transaction-modal-value">
              {formatNumber(selectedTransaction.conversionRateFinal, 5)}
            </span>
          </div>
        ) : null}
        {selectedTransaction.markupFee != null ? (
          <div className="transaction-modal-row">
            <span className="transaction-modal-label">Fee</span>
            <span className="transaction-modal-value">
              {formatNumber(selectedTransaction.markupFee, 2)}{" "}
              {selectedTransaction.targetCurrency || ""}
            </span>
          </div>
        ) : null}
        {selectedTransaction.transactionFee != null ? (
          <div className="transaction-modal-row">
            <span className="transaction-modal-label">Transaction Fee</span>
            <span className="transaction-modal-value">
              {formatNumber(selectedTransaction.transactionFee, 2)}{" "}
              {selectedTransaction.currency || ""}
            </span>
          </div>
        ) : null}
        <div className="transaction-modal-divider" />
        {selectedTransaction.deliveryMethod ? (
          <div className="transaction-modal-row">
            <span className="transaction-modal-label">Delivery Method</span>
            <span className="transaction-modal-value">
              {selectedTransaction.deliveryMethod}
            </span>
          </div>
        ) : null}
        {selectedTransaction.paymentMethod ? (
          <div className="transaction-modal-row">
            <span className="transaction-modal-label">Payment Method</span>
            <span className="transaction-modal-value">
              {selectedTransaction.paymentMethod}
            </span>
          </div>
        ) : null}
        {selectedTransaction.message ? (
          <div className="transaction-modal-row">
            <span className="transaction-modal-label">Message</span>
            <span className="transaction-modal-value">
              {selectedTransaction.message}
            </span>
          </div>
        ) : null}
        {selectedTransaction.type === "ADJUSTMENT" ? (
          <>
            <div className="transaction-modal-divider" />
            <div className="transaction-modal-row">
              <span className="transaction-modal-label">Reason</span>
              <span className="transaction-modal-value">
                {selectedTransaction.reason || "N/A"}
              </span>
            </div>
            {selectedTransaction.balanceBefore != null ? (
              <div className="transaction-modal-row">
                <span className="transaction-modal-label">Balance Before</span>
                <span className="transaction-modal-value">
                  {formatNumber(selectedTransaction.balanceBefore, 2)}{" "}
                  {selectedTransaction.currency || ""}
                </span>
              </div>
            ) : null}
            {selectedTransaction.balanceAfter != null ? (
              <div className="transaction-modal-row">
                <span className="transaction-modal-label">Balance After</span>
                <span className="transaction-modal-value">
                  {formatNumber(selectedTransaction.balanceAfter, 2)}{" "}
                  {selectedTransaction.currency || ""}
                </span>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    )}
  </Modal>
);

export default DetailModal;
